import { useStudentStore } from '@/shared/stores';
import type {
  JornadaFilterState,
  VisibleColumnsState,
} from '@/shared/models';
import { useCallback } from 'react';

/**
 * Hook Facade (Facade) para interactuar con el store de estudiantes.
 * Este es el ÚNICO lugar que debe importar y usar `useStudentStore` directamente.
 * Proporciona una API estable y segura para el resto de la aplicación.
 *
 * Cumple ISP: Interfaces segregadas y específicas para cada necesidad
 */
export const useAppStudents = () => {
  // 1. Selecciona cada pieza del estado de forma individual.
  // Esto es crucial para evitar re-renders innecesarios en los componentes que consumen el hook.
  const students = useStudentStore((state) => state.students);
  const careerOptions = useStudentStore((state) => state.careerOptions);
  const activeFilter = useStudentStore((state) => state.activeFilter);
  const searchQuery = useStudentStore((state) => state.searchQuery);
  const jornadaFilter = useStudentStore((state) => state.jornadaFilter);
  const visibleColumns = useStudentStore((state) => state.visibleColumns);
  const isLoading = useStudentStore((state) => state.isLoading);

  // 2. Selecciona las acciones del store
  const loadStudents = useStudentStore((state) => state.loadStudents);
  const setFilter = useStudentStore((state) => state.setFilter);
  const setSearchQuery = useStudentStore((state) => state.setSearchQuery);
  const setJornadaFilter = useStudentStore((state) => state.setJornadaFilter);
  const setVisibleColumns = useStudentStore(
    (state) => state.setVisibleColumns,
  );
  const setLoading = useStudentStore((state) => state.setLoading);
  const clearStudents = useStudentStore((state) => state.clearStudents);

  // 3. Memoiza las funciones de actualización para mantener una referencia estable.
  const handleSetFilter = useCallback(
    (filter: string) => {
      setFilter(filter);
    },
    [setFilter],
  );

  const handleSetSearchQuery = useCallback(
    (query: string) => {
      setSearchQuery(query);
    },
    [setSearchQuery],
  );

  const handleSetJornadaFilter = useCallback(
    (value: JornadaFilterState) => {
      setJornadaFilter(value);
    },
    [setJornadaFilter],
  );

  const handleSetVisibleColumns = useCallback(
    (value: VisibleColumnsState) => {
      setVisibleColumns(value);
    },
    [setVisibleColumns],
  );

  const handleSetLoading = useCallback(
    (loading: boolean) => {
      setLoading(loading);
    },
    [setLoading],
  );

  const handleClearStudents = useCallback(() => {
    clearStudents();
  }, [clearStudents]);

  // 4. Devuelve una API pública y limpia para que la usen los componentes.
  return {
    // Valores del estado
    students,
    careerOptions,
    activeFilter,
    searchQuery,
    jornadaFilter,
    visibleColumns,
    isLoading,

    // Acciones para modificar el estado
    loadStudents, // Esta no necesita memoización ya que es estable en Zustand
    setFilter: handleSetFilter,
    setSearchQuery: handleSetSearchQuery,
    setJornadaFilter: handleSetJornadaFilter,
    setVisibleColumns: handleSetVisibleColumns,
    setLoading: handleSetLoading,
    clearStudents: handleClearStudents,
  };
};
